import React, { useState, useRef, useEffect, useCallback } from 'react';
import JSZip from 'jszip';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// ─── Constants ────────────────────────────────────────────────────────────────
const ORBIT_RINGS = [
  { id: 'top',  label: 'Top',    elevation: 90,  color: '#f97316', targetFrames: 4,  description: 'Hold phone directly above the dish' },
  { id: 'high', label: 'High',   elevation: 55,  color: '#3b82f6', targetFrames: 12, description: 'Tilt phone at ~55° — slowly orbit around' },
  { id: 'mid',  label: 'Middle', elevation: 25,  color: '#10b981', targetFrames: 16, description: 'Hold phone at ~25° — slowly orbit around' },
  { id: 'low',  label: 'Low',    elevation: 5,   color: '#8b5cf6', targetFrames: 8,  description: 'Hold phone nearly level — slowly orbit around' },
];

const TOTAL_TARGET_FRAMES = ORBIT_RINGS?.reduce((s, r) => s + r?.targetFrames, 0); // 40

const SCAN_PHASES = [
  { id: 'idle',      label: 'Ready',      icon: 'Camera' },
  { id: 'scanning',  label: 'Scanning',   icon: 'Video' },
  { id: 'paused',    label: 'Paused',     icon: 'Pause' },
  { id: 'complete',  label: 'Complete',   icon: 'CheckCircle' },
];

// Frame quality thresholds
const QUALITY_GOOD    = 75;
const QUALITY_FAIR    = 45;
const FRAME_INTERVAL  = 800; // ms between auto-captures during recording

// Default cube proportions (normalized 0–1 relative to canvas)
const DEFAULT_CUBE = { x: 0.2, y: 0.15, w: 0.6, h: 0.65, depth: 0.18 };

// Handle size in px
const HANDLE_SIZE = 12;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getQualityColor = (score) => {
  if (score >= QUALITY_GOOD) return '#10b981';
  if (score >= QUALITY_FAIR) return '#f59e0b';
  return '#ef4444';
};

const getQualityLabel = (score) => {
  if (score >= QUALITY_GOOD) return 'Good';
  if (score >= QUALITY_FAIR) return 'Fair';
  return 'Poor';
};

// Estimate frame quality from ImageData (blur + brightness heuristics)
const estimateFrameQuality = (imageData) => {
  const d = imageData?.data;
  let brightness = 0;
  let edgeSum = 0;
  let w = imageData?.width;
  let h = imageData?.height;
  const step = 4;

  for (let y = 1; y < h - 1; y += step) {
    for (let x = 1; x < w - 1; x += step) {
      const i = (y * w + x) * 4;
      const gray = (d?.[i] * 0.299 + d?.[i + 1] * 0.587 + d?.[i + 2] * 0.114);
      brightness += gray;
      const top    = (d?.[((y-1)*w+x)*4] * 0.299 + d?.[((y-1)*w+x)*4+1] * 0.587 + d?.[((y-1)*w+x)*4+2] * 0.114);
      const bottom = (d?.[((y+1)*w+x)*4] * 0.299 + d?.[((y+1)*w+x)*4+1] * 0.587 + d?.[((y+1)*w+x)*4+2] * 0.114);
      const left   = (d?.[(y*w+x-1)*4] * 0.299 + d?.[(y*w+x-1)*4+1] * 0.587 + d?.[(y*w+x-1)*4+2] * 0.114);
      const right  = (d?.[(y*w+x+1)*4] * 0.299 + d?.[(y*w+x+1)*4+1] * 0.587 + d?.[(y*w+x+1)*4+2] * 0.114);
      edgeSum += Math.abs(4 * gray - top - bottom - left - right);
    }
  }

  const pixels = Math.ceil(w / step) * Math.ceil(h / step);
  const avgBrightness = brightness / pixels;
  const avgEdge = edgeSum / pixels;

  const brightScore = avgBrightness < 40 ? (avgBrightness / 40) * 50
    : avgBrightness > 220 ? Math.max(0, 100 - (avgBrightness - 220) * 2)
    : 100;

  const sharpScore = Math.min(100, (avgEdge / 12) * 100);

  return Math.round(brightScore * 0.4 + sharpScore * 0.6);
};

// Draw isometric 3D cube on canvas given normalized box coords
const drawCube3D = (ctx, w, h, cube, ringColor, isActive) => {
  const px = cube?.x * w;
  const py = cube?.y * h;
  const pw = cube?.w * w;
  const ph = cube?.h * h;
  const depthX = cube?.depth * w * 0.5;
  const depthY = cube?.depth * h * 0.25;

  // Front face corners
  const fl = px, fr = px + pw, ft = py, fb = py + ph;
  // Back face corners (offset by depth)
  const bl = fl + depthX, br = fr + depthX, bt = ft - depthY, bb = fb - depthY;

  const alpha = isActive ? 0.9 : 0.6;

  // Top face (parallelogram)
  ctx?.beginPath();
  ctx?.moveTo(fl, ft);
  ctx?.lineTo(bl, bt);
  ctx?.lineTo(br, bt);
  ctx?.lineTo(fr, ft);
  ctx?.closePath();
  ctx.fillStyle = `rgba(249,115,22,${isActive ? 0.18 : 0.1})`;
  ctx?.fill();
  ctx.strokeStyle = `rgba(249,115,22,${alpha})`;
  ctx.lineWidth = isActive ? 2 : 1.5;
  ctx?.stroke();

  // Right face (parallelogram)
  ctx?.beginPath();
  ctx?.moveTo(fr, ft);
  ctx?.lineTo(br, bt);
  ctx?.lineTo(br, bb);
  ctx?.lineTo(fr, fb);
  ctx?.closePath();
  ctx.fillStyle = `rgba(249,115,22,${isActive ? 0.12 : 0.06})`;
  ctx?.fill();
  ctx.strokeStyle = `rgba(249,115,22,${alpha})`;
  ctx.lineWidth = isActive ? 2 : 1.5;
  ctx?.stroke();

  // Front face
  ctx?.beginPath();
  ctx?.rect(fl, ft, pw, ph);
  ctx.fillStyle = `rgba(249,115,22,${isActive ? 0.06 : 0.03})`;
  ctx?.fill();
  ctx.strokeStyle = ringColor ? `${ringColor}${isActive ? 'ee' : '99'}` : `rgba(249,115,22,${alpha})`;
  ctx.lineWidth = isActive ? 2.5 : 1.5;
  ctx?.stroke();

  // Dashed depth edges (back-bottom and back-left)
  ctx?.setLineDash([4, 4]);
  ctx.strokeStyle = `rgba(249,115,22,0.4)`;
  ctx.lineWidth = 1;
  // back-left vertical
  ctx?.beginPath(); ctx?.moveTo(bl, bt); ctx?.lineTo(bl, bb); ctx?.stroke();
  // back-bottom horizontal
  ctx?.beginPath(); ctx?.moveTo(bl, bb); ctx?.lineTo(br, bb); ctx?.stroke();
  ctx?.setLineDash([]);

  // Corner brackets on front face
  const cl = 14;
  ctx.strokeStyle = isActive ? '#f97316' : 'rgba(249,115,22,0.7)';
  ctx.lineWidth = isActive ? 3 : 2;
  [
    [fl, ft,  cl, 0,  0,  cl],
    [fr, ft, -cl, 0,  0,  cl],
    [fl, fb,  cl, 0,  0, -cl],
    [fr, fb, -cl, 0,  0, -cl],
  ]?.forEach(([x, y, hx, , , vy]) => {
    ctx?.beginPath();
    ctx?.moveTo(x + hx, y); ctx?.lineTo(x, y); ctx?.lineTo(x, y + vy);
    ctx?.stroke();
  });

  // Resize handles (8 corners of front face)
  const handles = getCubeHandles(cube, w, h);
  handles?.forEach(({ hx, hy }) => {
    ctx?.beginPath();
    ctx?.arc(hx, hy, HANDLE_SIZE / 2, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(249,115,22,0.9)';
    ctx?.fill();
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 1.5;
    ctx?.stroke();
  });

  // Depth handle (top-right back corner)
  ctx?.beginPath();
  ctx?.arc(br, bt, HANDLE_SIZE / 2, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(251,191,36,0.9)';
  ctx?.fill();
  ctx.strokeStyle = 'white';
  ctx.lineWidth = 1.5;
  ctx?.stroke();

  // Label
  ctx.font = 'bold 10px sans-serif';
  ctx.fillStyle = 'rgba(249,115,22,0.9)';
  ctx?.fillText('SCAN ZONE', fl + 6, ft - 6);
};

// Returns 8 handle positions for front face corners + midpoints
const getCubeHandles = (cube, w, h) => {
  const px = cube?.x * w, py = cube?.y * h;
  const pw = cube?.w * w, ph = cube?.h * h;
  return [
    { id: 'tl', hx: px,        hy: py,        cursor: 'nw-resize' },
    { id: 'tm', hx: px + pw/2, hy: py,        cursor: 'n-resize'  },
    { id: 'tr', hx: px + pw,   hy: py,        cursor: 'ne-resize' },
    { id: 'ml', hx: px,        hy: py + ph/2, cursor: 'w-resize'  },
    { id: 'mr', hx: px + pw,   hy: py + ph/2, cursor: 'e-resize'  },
    { id: 'bl', hx: px,        hy: py + ph,   cursor: 'sw-resize' },
    { id: 'bm', hx: px + pw/2, hy: py + ph,   cursor: 's-resize'  },
    { id: 'br', hx: px + pw,   hy: py + ph,   cursor: 'se-resize' },
  ];
};

// Returns depth handle position
const getDepthHandle = (cube, w, h) => {
  const depthX = cube?.depth * w * 0.5;
  const depthY = cube?.depth * h * 0.25;
  return { hx: (cube?.x + cube?.w) * w + depthX, hy: cube?.y * h - depthY };
};

// Hit-test a point against handles
const hitTestHandle = (px, py, cube, w, h) => {
  const handles = getCubeHandles(cube, w, h);
  for (const handle of handles) {
    const dx = px - handle?.hx, dy = py - handle?.hy;
    if (Math.sqrt(dx*dx + dy*dy) <= HANDLE_SIZE) return { type: 'resize', id: handle?.id };
  }
  // Depth handle
  const dh = getDepthHandle(cube, w, h);
  const ddx = px - dh?.hx, ddy = py - dh?.hy;
  if (Math.sqrt(ddx*ddx + ddy*ddy) <= HANDLE_SIZE) return { type: 'depth' };
  // Inside front face = drag
  const fx = cube?.x * w, fy = cube?.y * h, fw = cube?.w * w, fh = cube?.h * h;
  if (px >= fx && px <= fx + fw && py >= fy && py <= fy + fh) return { type: 'move' };
  return null;
};

// Apply resize delta to cube
const applyResize = (cube, handleId, dx, dy, cw, ch) => {
  let { x, y, w, h, depth } = cube;
  const ndx = dx / cw, ndy = dy / ch;
  const minW = 0.1, minH = 0.1;

  if (handleId?.includes('l')) { const nw = w - ndx; if (nw > minW) { x += ndx; w = nw; } }
  if (handleId?.includes('r')) { const nw = w + ndx; if (nw > minW) w = nw; }
  if (handleId?.includes('t')) { const nh = h - ndy; if (nh > minH) { y += ndy; h = nh; } }
  if (handleId?.includes('b')) { const nh = h + ndy; if (nh > minH) h = nh; }

  // Clamp to canvas
  x = Math.max(0, Math.min(x, 1 - w));
  y = Math.max(0, Math.min(y, 1 - h));
  w = Math.min(w, 1 - x);
  h = Math.min(h, 1 - y);

  return { x, y, w, h, depth };
};

// ─── Component ────────────────────────────────────────────────────────────────
const DishScannerModal = ({ isOpen, onClose, dishName, onScanComplete }) => {
  const videoRef       = useRef(null);
  const captureCanvas  = useRef(null);
  const overlayCanvas  = useRef(null);
  const streamRef      = useRef(null);
  const animFrameRef   = useRef(null);
  const roRef          = useRef(null);
  const intervalRef    = useRef(null);
  const frameCountRef  = useRef(0);
  const framesRef      = useRef([]);

  // Cube interaction refs
  const cubeRef        = useRef({ ...DEFAULT_CUBE });
  const dragRef        = useRef(null); // { type, id, startX, startY, startCube }
  const cursorRef      = useRef('default');

  const [phase, setPhase]               = useState('idle');
  const [cameraReady, setCameraReady]   = useState(false);
  const [cameraError, setCameraError]   = useState('');
  const [currentRingIdx, setCurrentRingIdx] = useState(0);
  const [frames, setFrames]             = useState([]);
  const [liveQuality, setLiveQuality]   = useState(0);
  const [ringProgress, setRingProgress] = useState({ top: 0, high: 0, mid: 0, low: 0 });
  const [isZipping, setIsZipping]       = useState(false);
  const [flashEffect, setFlashEffect]   = useState(false);
  const [scanDuration, setScanDuration] = useState(0);
  const [cube, setCube]                 = useState({ ...DEFAULT_CUBE });
  const scanStartRef = useRef(null);
  const durationTimerRef = useRef(null);

  const currentRing = ORBIT_RINGS?.[currentRingIdx];
  const totalFrames = frames?.length;
  const overallProgress = Math.min(100, Math.round((totalFrames / TOTAL_TARGET_FRAMES) * 100));
  const isScanning = phase === 'scanning';
  const isComplete = phase === 'complete';

  // Keep cubeRef in sync with state
  useEffect(() => { cubeRef.current = cube; }, [cube]);

  // ─── Camera ─────────────────────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    setCameraError('');
    try {
      const stream = await navigator.mediaDevices?.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef?.current) {
        videoRef.current.srcObject = stream;
        await videoRef?.current?.play();
        setCameraReady(true);
      }
    } catch {
      setCameraError('Cannot access camera. Please check permissions.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (animFrameRef?.current) cancelAnimationFrame(animFrameRef?.current);
    if (roRef?.current) { roRef?.current?.disconnect(); roRef.current = null; }
    if (intervalRef?.current) { clearInterval(intervalRef?.current); intervalRef.current = null; }
    if (durationTimerRef?.current) { clearInterval(durationTimerRef?.current); durationTimerRef.current = null; }
    if (streamRef?.current) {
      streamRef?.current?.getTracks()?.forEach(t => t?.stop());
      streamRef.current = null;
    }
    setCameraReady(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      frameCountRef.current = 0;
      framesRef.current = [];
      setFrames([]);
      setPhase('idle');
      setCurrentRingIdx(0);
      setRingProgress({ top: 0, high: 0, mid: 0, low: 0 });
      setScanDuration(0);
      setCube({ ...DEFAULT_CUBE });
      cubeRef.current = { ...DEFAULT_CUBE };
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [isOpen, startCamera, stopCamera]);

  // ─── Sync overlay canvas to video rect ──────────────────────────────────
  const syncCanvas = useCallback(() => {
    const video = videoRef?.current;
    const canvas = overlayCanvas?.current;
    if (!video || !canvas) return;
    const rect = video?.getBoundingClientRect();
    if (rect?.width > 0 && rect?.height > 0) {
      canvas.width  = Math.round(rect?.width);
      canvas.height = Math.round(rect?.height);
      canvas.style.width  = rect?.width + 'px';
      canvas.style.height = rect?.height + 'px';
    }
  }, []);

  useEffect(() => {
    if (!cameraReady) return;
    syncCanvas();
    roRef.current = new ResizeObserver(syncCanvas);
    roRef?.current?.observe(videoRef?.current);
    return () => { if (roRef?.current) { roRef?.current?.disconnect(); roRef.current = null; } };
  }, [cameraReady, syncCanvas]);

  // ─── Live quality meter ──────────────────────────────────────────────────
  useEffect(() => {
    if (!cameraReady) return;
    const qCanvas = document.createElement('canvas');
    qCanvas.width  = 160;
    qCanvas.height = 90;
    const qCtx = qCanvas?.getContext('2d');

    const loop = () => {
      if (videoRef?.current && videoRef?.current?.readyState >= 2) {
        qCtx?.drawImage(videoRef?.current, 0, 0, 160, 90);
        const imgData = qCtx?.getImageData(0, 0, 160, 90);
        setLiveQuality(estimateFrameQuality(imgData));
      }
      animFrameRef.current = requestAnimationFrame(loop);
    };
    animFrameRef.current = requestAnimationFrame(loop);
    return () => { if (animFrameRef?.current) cancelAnimationFrame(animFrameRef?.current); };
  }, [cameraReady]);

  // ─── Cube pointer interaction ────────────────────────────────────────────
  const getCanvasPoint = useCallback((e) => {
    const canvas = overlayCanvas?.current;
    if (!canvas) return { px: 0, py: 0 };
    const rect = canvas?.getBoundingClientRect();
    const clientX = e?.touches ? e?.touches?.[0]?.clientX : e?.clientX;
    const clientY = e?.touches ? e?.touches?.[0]?.clientY : e?.clientY;
    return {
      px: (clientX - rect?.left) * (canvas?.width / rect?.width),
      py: (clientY - rect?.top)  * (canvas?.height / rect?.height),
    };
  }, []);

  const handlePointerDown = useCallback((e) => {
    const canvas = overlayCanvas?.current;
    if (!canvas) return;
    const { px, py } = getCanvasPoint(e);
    const hit = hitTestHandle(px, py, cubeRef?.current, canvas?.width, canvas?.height);
    if (!hit) return;
    e?.preventDefault();
    dragRef.current = { ...hit, startX: px, startY: py, startCube: { ...cubeRef?.current } };
  }, [getCanvasPoint]);

  const handlePointerMove = useCallback((e) => {
    const canvas = overlayCanvas?.current;
    if (!canvas) return;
    const { px, py } = getCanvasPoint(e);

    if (dragRef?.current) {
      e?.preventDefault();
      const { type, id, startX, startY, startCube } = dragRef?.current;
      const dx = px - startX, dy = py - startY;
      const cw = canvas?.width, ch = canvas?.height;

      let newCube = { ...startCube };

      if (type === 'move') {
        newCube.x = Math.max(0, Math.min(1 - startCube?.w, startCube?.x + dx / cw));
        newCube.y = Math.max(0, Math.min(1 - startCube?.h, startCube?.y + dy / ch));
      } else if (type === 'resize') {
        newCube = applyResize(startCube, id, dx, dy, cw, ch);
      } else if (type === 'depth') {
        newCube.depth = Math.max(0.05, Math.min(0.4, startCube?.depth + dx / cw));
      }

      cubeRef.current = newCube;
      setCube(newCube);
    } else {
      // Update cursor
      const hit = hitTestHandle(px, py, cubeRef?.current, canvas?.width, canvas?.height);
      const handles = getCubeHandles(cubeRef?.current, canvas?.width, canvas?.height);
      if (hit?.type === 'resize') {
        let h = handles?.find(hh => hh?.id === hit?.id);
        canvas.style.cursor = h?.cursor || 'crosshair';
      } else if (hit?.type === 'depth') {
        canvas.style.cursor = 'ew-resize';
      } else if (hit?.type === 'move') {
        canvas.style.cursor = 'move';
      } else {
        canvas.style.cursor = 'default';
      }
    }
  }, [getCanvasPoint]);

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  // Attach pointer events to overlay canvas
  useEffect(() => {
    const canvas = overlayCanvas?.current;
    if (!canvas || !cameraReady) return;
    canvas?.addEventListener('mousedown', handlePointerDown);
    canvas?.addEventListener('mousemove', handlePointerMove);
    canvas?.addEventListener('mouseup', handlePointerUp);
    canvas?.addEventListener('mouseleave', handlePointerUp);
    canvas?.addEventListener('touchstart', handlePointerDown, { passive: false });
    canvas?.addEventListener('touchmove', handlePointerMove, { passive: false });
    canvas?.addEventListener('touchend', handlePointerUp);
    return () => {
      canvas?.removeEventListener('mousedown', handlePointerDown);
      canvas?.removeEventListener('mousemove', handlePointerMove);
      canvas?.removeEventListener('mouseup', handlePointerUp);
      canvas?.removeEventListener('mouseleave', handlePointerUp);
      canvas?.removeEventListener('touchstart', handlePointerDown);
      canvas?.removeEventListener('touchmove', handlePointerMove);
      canvas?.removeEventListener('touchend', handlePointerUp);
    };
  }, [cameraReady, handlePointerDown, handlePointerMove, handlePointerUp]);

  // ─── Capture a single frame (cropped to cube region) ────────────────────
  const captureFrame = useCallback((ring) => {
    const video = videoRef?.current;
    const canvas = captureCanvas?.current;
    if (!video || !canvas || video?.readyState < 2) return null;

    const vw = video?.videoWidth  || 1280;
    const vh = video?.videoHeight || 720;

    // Crop region based on cube proportions
    const c = cubeRef?.current;
    const cropX = Math.round(c?.x * vw);
    const cropY = Math.round(c?.y * vh);
    const cropW = Math.round(c?.w * vw);
    const cropH = Math.round(c?.h * vh);

    canvas.width  = cropW;
    canvas.height = cropH;
    const ctx = canvas?.getContext('2d');
    // Draw only the cube region
    ctx?.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

    // Quality from cropped region
    const imgData = ctx?.getImageData(0, 0, Math.min(cropW, 320), Math.min(cropH, 180));
    const quality = estimateFrameQuality(imgData);

    const dataUrl = canvas?.toDataURL('image/jpeg', 0.92);
    return { dataUrl, quality, ring: ring?.id, timestamp: Date.now() };
  }, []);

  // ─── Ring index from frame count ─────────────────────────────────────────
  const getRingFromCount = (count) => {
    if (count < 4)  return ORBIT_RINGS?.[0];
    if (count < 16) return ORBIT_RINGS?.[1];
    if (count < 32) return ORBIT_RINGS?.[2];
    return ORBIT_RINGS?.[3];
  };

  // ─── Shared interval body ────────────────────────────────────────────────
  const runCaptureInterval = useCallback(() => {
    intervalRef.current = setInterval(() => {
      const ring = getRingFromCount(frameCountRef?.current);

      const frame = captureFrame(ring);
      if (!frame || frame?.quality < 20) return;

      framesRef.current = [...framesRef?.current, frame];
      frameCountRef.current += 1;

      setFlashEffect(true);
      setTimeout(() => setFlashEffect(false), 120);

      setFrames([...framesRef?.current]);
      setRingProgress(prev => ({
        ...prev,
        [ring?.id]: Math.min(
          ORBIT_RINGS?.find(r => r?.id === ring?.id)?.targetFrames,
          (prev?.[ring?.id] || 0) + 1
        ),
      }));

      const idx = frameCountRef?.current < 4 ? 0
        : frameCountRef?.current < 16 ? 1
        : frameCountRef?.current < 32 ? 2
        : 3;
      setCurrentRingIdx(Math.min(idx, ORBIT_RINGS?.length - 1));

      // NO auto-complete — user decides when to finish
    }, FRAME_INTERVAL);
  }, [captureFrame]);

  // ─── Start / Stop scanning ───────────────────────────────────────────────
  const startScanning = useCallback(() => {
    if (!cameraReady) return;
    setPhase('scanning');
    scanStartRef.current = Date.now();

    durationTimerRef.current = setInterval(() => {
      setScanDuration(Math.floor((Date.now() - scanStartRef?.current) / 1000));
    }, 1000);

    runCaptureInterval();
  }, [cameraReady, runCaptureInterval]);

  const pauseScanning = useCallback(() => {
    if (intervalRef?.current) { clearInterval(intervalRef?.current); intervalRef.current = null; }
    if (durationTimerRef?.current) { clearInterval(durationTimerRef?.current); durationTimerRef.current = null; }
    setPhase('paused');
  }, []);

  const resumeScanning = useCallback(() => {
    if (phase !== 'paused') return;
    scanStartRef.current = Date.now() - scanDuration * 1000;
    durationTimerRef.current = setInterval(() => {
      setScanDuration(Math.floor((Date.now() - scanStartRef?.current) / 1000));
    }, 1000);
    runCaptureInterval();
    setPhase('scanning');
  }, [phase, scanDuration, runCaptureInterval]);

  const resetScan = useCallback(() => {
    if (intervalRef?.current) { clearInterval(intervalRef?.current); intervalRef.current = null; }
    if (durationTimerRef?.current) { clearInterval(durationTimerRef?.current); durationTimerRef.current = null; }
    frameCountRef.current = 0;
    framesRef.current = [];
    setFrames([]);
    setPhase('idle');
    setCurrentRingIdx(0);
    setRingProgress({ top: 0, high: 0, mid: 0, low: 0 });
    setScanDuration(0);
  }, []);

  const finishEarly = useCallback(() => {
    if (intervalRef?.current) { clearInterval(intervalRef?.current); intervalRef.current = null; }
    if (durationTimerRef?.current) { clearInterval(durationTimerRef?.current); durationTimerRef.current = null; }
    setPhase('complete');
  }, []);

  // ─── Export ──────────────────────────────────────────────────────────────
  const handleExport = useCallback(() => {
    onScanComplete?.({
      dishName,
      frames: framesRef?.current,
      capturedAt: new Date()?.toISOString(),
      totalFrames: framesRef?.current?.length,
    });
    onClose();
  }, [dishName, onScanComplete, onClose]);

  const handleDownloadZip = useCallback(async () => {
    setIsZipping(true);
    try {
      const zip = new JSZip();
      const folder = zip?.folder('images');
      const safeName = dishName?.replace(/\s+/g, '_')?.replace(/[^a-zA-Z0-9_-]/g, '') || 'dish';

      framesRef?.current?.forEach((frame, i) => {
        const num = String(i + 1)?.padStart(4, '0');
        const b64 = frame?.dataUrl?.replace(/^data:image\/\w+;base64,/, '');
        folder?.file(`frame_${num}.jpg`, b64, { base64: true });
      });

      const goodFrames  = framesRef?.current?.filter(f => f?.quality >= QUALITY_GOOD)?.length;
      const fairFrames  = framesRef?.current?.filter(f => f?.quality >= QUALITY_FAIR && f?.quality < QUALITY_GOOD)?.length;
      const avgQuality  = framesRef?.current?.length
        ? Math.round(framesRef?.current?.reduce((s, f) => s + f?.quality, 0) / framesRef?.current?.length)
        : 0;

      const metadata = {
        capture_tool: 'QR Menu AR — Orbit Scanner v4',
        subject: dishName,
        captured_at: new Date()?.toISOString(),
        scan_duration_seconds: scanDuration,
        total_frames: framesRef?.current?.length,
        scan_zone: cubeRef?.current,
        quality_summary: { average: avgQuality, good: goodFrames, fair: fairFrames, poor: framesRef?.current?.length - goodFrames - fairFrames },
        orbit_rings: ORBIT_RINGS?.map(r => ({
          id: r?.id, elevation_deg: r?.elevation,
          frames_captured: framesRef?.current?.filter(f => f?.ring === r?.id)?.length,
          target_frames: r?.targetFrames,
        })),
        frames: framesRef?.current?.map((f, i) => ({
          filename: `frame_${String(i + 1)?.padStart(4, '0')}.jpg`,
          ring: f?.ring,
          quality_score: f?.quality,
          quality_label: getQualityLabel(f?.quality),
          timestamp_ms: f?.timestamp,
        })),
        luma_ai_instructions: [
          '1. Go to https://lumalabs.ai/capture',
          '2. Click "New Capture" → Object mode',
          '3. Choose "Upload Images"',
          '4. Upload ALL frame_XXXX.jpg files from the images/ folder',
          '5. Submit — processing takes ~15–30 min',
          '6. Download the .glb file for AR use',
        ],
      };

      zip?.file('metadata.json', JSON.stringify(metadata, null, 2));
      zip?.file('README.txt', [
        `# ${dishName} — Luma AI Orbit Capture`,
        '',
        `Captured: ${new Date()?.toLocaleString()}`,
        `Total frames: ${framesRef?.current?.length}`,
        `Average quality: ${avgQuality}/100`,
        '',
        '## Upload to Luma AI',
        '1. Visit https://lumalabs.ai/capture',
        '2. New Capture → Object → Upload Images',
        '3. Select all files from images/ folder',
        '4. Submit and wait ~15–30 min',
      ]?.join('\n'));

      const blob = await zip?.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${safeName}_luma_orbit.zip`;
      document.body?.appendChild(a);
      a?.click();
      document.body?.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('ZIP export failed:', err);
    }
    setIsZipping(false);
  }, [dishName, scanDuration]);

  // ─── Coverage Map (polar SVG) ────────────────────────────────────────────
  const CoverageMap = () => {
    const cx = 80, cy = 80, r = 60;
    const ringRadii = [15, 30, 45, 60];

    return (
      <svg width="160" height="160" viewBox="0 0 160 160">
        {ringRadii?.map((radius, i) => (
          <circle key={i} cx={cx} cy={cy} r={radius} fill="none"
            stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
        ))}
        {[0, 45, 90, 135]?.map(deg => {
          const rad = (deg * Math.PI) / 180;
          return (
            <line key={deg}
              x1={cx} y1={cy}
              x2={cx + r * Math.cos(rad)} y2={cy + r * Math.sin(rad)}
              stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
          );
        })}
        {framesRef?.current?.map((frame, i) => {
          const ringIdx = ORBIT_RINGS?.findIndex(r => r?.id === frame?.ring);
          const radius  = ringRadii?.[ringIdx] || 30;
          const angle   = (i / Math.max(framesRef?.current?.length, 1)) * 2 * Math.PI;
          let x = cx + radius * Math.cos(angle);
          let y = cy + radius * Math.sin(angle);
          const color = ORBIT_RINGS?.[ringIdx]?.color || '#fff';
          return <circle key={i} cx={x} cy={y} r={3} fill={color} opacity={0.85} />;
        })}
        <circle cx={cx} cy={cy} r={4} fill="#f97316" />
        {ORBIT_RINGS?.map((ring, i) => (
          <text key={ring?.id}
            x={cx + ringRadii?.[i] + 3} y={cy - 3}
            fill={ring?.color} fontSize="7" opacity="0.8">{ring?.label}</text>
        ))}
      </svg>
    );
  };

  // ─── Orbit Guide Indicator ───────────────────────────────────────────────
  const OrbitGuide = () => {
    const progress = ringProgress?.[currentRing?.id] || 0;
    const target   = currentRing?.targetFrames || 1;
    const pct      = Math.min(100, (progress / target) * 100);
    const circumference = 2 * Math.PI * 36;
    const dash = (pct / 100) * circumference;

    return (
      <div className="flex flex-col items-center gap-2">
        <div className="relative w-20 h-20">
          <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
            <circle cx="40" cy="40" r="36" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
            <circle cx="40" cy="40" r="36" fill="none"
              stroke={currentRing?.color || '#f97316'} strokeWidth="6"
              strokeDasharray={`${dash} ${circumference}`}
              strokeLinecap="round"
              style={{ transition: 'stroke-dasharray 0.4s ease' }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-white text-sm font-bold">{progress}</span>
            <span className="text-white/50 text-[9px]">/{target}</span>
          </div>
        </div>
        <div className="text-center">
          <div className="text-xs font-semibold" style={{ color: currentRing?.color }}>{currentRing?.label} Ring</div>
          <div className="text-[10px] text-white/50">{Math.round(currentRing?.elevation)}° elevation</div>
        </div>
      </div>
    );
  };

  // ─── Quality Bar ─────────────────────────────────────────────────────────
  const QualityBar = ({ score }) => {
    const color = getQualityColor(score);
    const label = getQualityLabel(score);
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-200"
            style={{ width: `${score}%`, backgroundColor: color }} />
        </div>
        <span className="text-[10px] font-medium w-8" style={{ color }}>{label}</span>
      </div>
    );
  };

  // ─── Cube Dimensions Display ─────────────────────────────────────────────
  const CubeDimensions = () => (
    <div className="grid grid-cols-3 gap-1 text-center">
      {[
        { label: 'W', value: Math.round(cube?.w * 100) + '%' },
        { label: 'H', value: Math.round(cube?.h * 100) + '%' },
        { label: 'D', value: Math.round(cube?.depth * 100) + '%' },
      ]?.map(({ label, value }) => (
        <div key={label} className="bg-white/5 rounded-lg py-1.5 border border-white/10">
          <div className="text-xs font-bold text-orange-400">{value}</div>
          <div className="text-[9px] text-white/40">{label}</div>
        </div>
      ))}
    </div>
  );

  // ─── Scanning Overlay (drawn on canvas each frame) ───────────────────────
  useEffect(() => {
    const canvas = overlayCanvas?.current;
    if (!canvas || !cameraReady) return;
    const ctx = canvas?.getContext('2d');
    let w = canvas?.width, h = canvas?.height;
    if (!w || !h) return;

    ctx?.clearRect(0, 0, w, h);

    // Always draw the 3D cube (interactive)
    const isActive = isScanning || phase === 'paused';
    drawCube3D(ctx, w, h, cubeRef?.current, currentRing?.color, isActive);

    if (isActive) {
      // Scanning border pulse
      const alpha = isScanning ? 0.4 + 0.2 * Math.sin(Date.now() / 300) : 0.2;
      ctx.strokeStyle = `rgba(249,115,22,${alpha})`;
      ctx.lineWidth = 2;
      ctx?.strokeRect(2, 2, w - 4, h - 4);

      // Scan line (only while scanning) — constrained to cube front face
      if (isScanning) {
        const fx = cubeRef?.current?.x * w;
        const fy = cubeRef?.current?.y * h;
        const fw = cubeRef?.current?.w * w;
        const fh = cubeRef?.current?.h * h;
        const scanY = fy + ((Date.now() / 2000) % 1) * fh;
        const grad = ctx?.createLinearGradient(0, scanY - 16, 0, scanY + 16);
        grad?.addColorStop(0, 'rgba(249,115,22,0)');
        grad?.addColorStop(0.5, 'rgba(249,115,22,0.35)');
        grad?.addColorStop(1, 'rgba(249,115,22,0)');
        ctx.fillStyle = grad;
        ctx?.fillRect(fx, scanY - 16, fw, 32);
      }

      // Ring color strip at top of cube
      const ringColor = currentRing?.color || '#f97316';
      ctx.fillStyle = ringColor + '44';
      ctx?.fillRect(cubeRef?.current?.x * w, cubeRef?.current?.y * h, cubeRef?.current?.w * w, 3);
    }
  });

  // ─── Format duration ─────────────────────────────────────────────────────
  const formatDuration = (s) => `${String(Math.floor(s / 60))?.padStart(2, '0')}:${String(s % 60)?.padStart(2, '0')}`;

  if (!isOpen) return null;

  const goodCount = frames?.filter(f => f?.quality >= QUALITY_GOOD)?.length;
  const avgQuality = frames?.length ? Math.round(frames?.reduce((s, f) => s + f?.quality, 0) / frames?.length) : 0;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-sm">
      <div className="relative w-full max-w-5xl mx-2 bg-[#0a0a0a] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[97vh] border border-white/10">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-orange-500/20 rounded-lg p-2">
              <Icon name="ScanLine" size={18} color="#f97316" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white">Orbit Scan — {dishName}</h2>
              <p className="text-[11px] text-white/40">Continuous capture · Luma AI photogrammetry</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {(isScanning || phase === 'paused') && (
              <div className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1">
                <div className={`w-2 h-2 rounded-full ${isScanning ? 'bg-red-500 animate-pulse' : 'bg-yellow-500'}`} />
                <span className="text-xs text-white/70 font-mono">{formatDuration(scanDuration)}</span>
              </div>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
              <Icon name="X" size={16} color="rgba(255,255,255,0.5)" />
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row flex-1 overflow-hidden min-h-0">

          {/* ── Camera Panel ── */}
          <div className="flex-1 flex flex-col bg-black relative min-h-[280px]">
            <div className="relative flex-1 overflow-hidden select-none flex items-center justify-center">
              <video ref={videoRef} className="w-full h-full object-contain" playsInline muted />

              {/* Overlay canvas — pointer-events enabled for cube interaction */}
              <canvas ref={overlayCanvas}
                className="absolute"
                style={{ top: 0, left: 0, pointerEvents: cameraReady ? 'auto' : 'none' }} />

              {/* Hidden capture canvas */}
              <canvas ref={captureCanvas} className="hidden" />

              {/* Flash */}
              {flashEffect && (
                <div className="absolute inset-0 bg-white/25 pointer-events-none" />
              )}

              {/* Camera error */}
              {cameraError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/95 text-white text-center px-6 z-10">
                  <Icon name="CameraOff" size={36} className="mb-3 opacity-40" />
                  <p className="text-sm mb-4 text-white/70">{cameraError}</p>
                  <Button onClick={startCamera} size="sm" iconName="RefreshCw">Retry</Button>
                </div>
              )}

              {/* Idle overlay */}
              {phase === 'idle' && cameraReady && (
                <div className="bg-black/75 backdrop-blur-sm rounded-xl px-4 py-3 text-center border border-white/10 whitespace-nowrap">
                  <p className="text-white/70 text-xs">
                    <span className="text-orange-400 font-semibold">Drag</span> the cube to position ·{' '}
                    <span className="text-orange-400 font-semibold">Handles</span> to resize ·{' '}
                    <span className="text-yellow-400 font-semibold">Yellow dot</span> for depth
                  </p>
                </div>
              )}

              {/* Scanning HUD */}
              {(isScanning || phase === 'paused') && (
                <>
                  {/* Top instruction bar */}
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
                    <div className="bg-black/75 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2 border border-white/10">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: currentRing?.color }} />
                      <span className="text-white text-xs font-medium">{currentRing?.description}</span>
                    </div>
                  </div>

                  {/* Bottom-left: live quality */}
                  <div className="absolute bottom-3 left-3 z-10 pointer-events-none">
                    <div className="bg-black/70 backdrop-blur-sm rounded-xl p-2.5 border border-white/10 w-36">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] text-white/50 uppercase tracking-wide">Frame Quality</span>
                        <span className="text-[10px] font-bold" style={{ color: getQualityColor(liveQuality) }}>{liveQuality}</span>
                      </div>
                      <QualityBar score={liveQuality} />
                    </div>
                  </div>

                  {/* Bottom-right: frame counter */}
                  <div className="absolute bottom-3 right-3 z-10 pointer-events-none">
                    <div className="bg-black/70 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/10 text-center">
                      <div className="text-xl font-bold text-white font-mono">{totalFrames}</div>
                      <div className="text-[10px] text-white/40">frames</div>
                    </div>
                  </div>
                </>
              )}

              {/* Complete overlay */}
              {isComplete && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 pointer-events-none z-10">
                  <div className="bg-black/80 backdrop-blur-sm rounded-2xl px-8 py-6 text-center border border-green-500/30">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 border-2 border-green-400 flex items-center justify-center mx-auto mb-4">
                      <Icon name="CheckCircle" size={32} color="#4ade80" />
                    </div>
                    <p className="text-white text-lg font-bold mb-1">Scan Complete!</p>
                    <p className="text-white/60 text-sm">{totalFrames} frames captured</p>
                    <p className="text-white/40 text-xs mt-1">Avg quality: {avgQuality}/100 · {goodCount} good frames</p>
                  </div>
                </div>
              )}
            </div>

            {/* ── Controls Bar ── */}
            <div className="flex items-center justify-center gap-3 py-4 px-4 bg-black/95 border-t border-white/10 flex-shrink-0">
              {phase === 'idle' && cameraReady && (
                <button onClick={startScanning}
                  className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-semibold text-sm px-6 py-3 rounded-full transition-colors shadow-lg shadow-orange-500/30">
                  <Icon name="Video" size={16} color="white" />
                  Start Scan
                </button>
              )}

              {isScanning && (
                <>
                  <button onClick={pauseScanning}
                    className="flex items-center gap-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 border border-yellow-500/30 font-medium text-sm px-4 py-2.5 rounded-full transition-colors">
                    <Icon name="Pause" size={14} color="#facc15" />
                    Pause
                  </button>
                  <button onClick={finishEarly}
                    className="flex items-center gap-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30 font-medium text-sm px-4 py-2.5 rounded-full transition-colors">
                    <Icon name="CheckCircle" size={14} color="#4ade80" />
                    Finish
                  </button>
                  <button onClick={resetScan}
                    className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white/50 border border-white/10 font-medium text-sm px-4 py-2.5 rounded-full transition-colors">
                    <Icon name="RotateCcw" size={14} color="rgba(255,255,255,0.5)" />
                    Reset
                  </button>
                </>
              )}

              {phase === 'paused' && (
                <>
                  <button onClick={resumeScanning}
                    className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors">
                    <Icon name="Play" size={14} color="white" />
                    Resume
                  </button>
                  <button onClick={finishEarly}
                    className="flex items-center gap-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30 font-medium text-sm px-4 py-2.5 rounded-full transition-colors">
                    <Icon name="CheckCircle" size={14} color="#4ade80" />
                    Finish
                  </button>
                  <button onClick={resetScan}
                    className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white/50 border border-white/10 font-medium text-sm px-4 py-2.5 rounded-full transition-colors">
                    <Icon name="RotateCcw" size={14} color="rgba(255,255,255,0.5)" />
                    Reset
                  </button>
                </>
              )}

              {isComplete && (
                <button onClick={resetScan}
                  className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white/50 border border-white/10 font-medium text-sm px-4 py-2.5 rounded-full transition-colors">
                  <Icon name="RotateCcw" size={14} color="rgba(255,255,255,0.5)" />
                  Scan Again
                </button>
              )}
            </div>
          </div>

          {/* ── Right Panel ── */}
          <div className="w-full lg:w-72 flex flex-col bg-[#111] border-t lg:border-t-0 lg:border-l border-white/10 overflow-y-auto">

            {/* Overall progress */}
            <div className="px-4 py-4 border-b border-white/10 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-white/50 uppercase tracking-wide">Overall Coverage</span>
                <span className="text-xs font-bold text-white">{overallProgress}%</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded-full transition-all duration-500"
                  style={{ width: `${overallProgress}%` }} />
              </div>
              <div className="flex justify-between mt-1.5">
                <span className="text-[10px] text-white/30">{totalFrames} frames captured</span>
                <span className="text-[10px] text-white/30">Target: {TOTAL_TARGET_FRAMES}+</span>
              </div>
            </div>

            {/* Scan Zone dimensions */}
            <div className="px-4 py-3 border-b border-white/10 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] text-white/40 uppercase tracking-wide">Scan Zone (Cube)</p>
                <button
                  onClick={() => { setCube({ ...DEFAULT_CUBE }); cubeRef.current = { ...DEFAULT_CUBE }; }}
                  className="text-[9px] text-orange-400/70 hover:text-orange-400 transition-colors"
                >Reset</button>
              </div>
              <CubeDimensions />
              <p className="text-[9px] text-white/30 mt-2 text-center">
                Drag cube · Orange handles resize · Yellow dot = depth
              </p>
            </div>

            {/* Coverage Map */}
            <div className="px-4 py-3 border-b border-white/10 flex-shrink-0">
              <p className="text-[10px] text-white/40 uppercase tracking-wide mb-2">Coverage Map</p>
              <div className="flex items-center justify-center bg-black/40 rounded-xl p-2">
                <CoverageMap />
              </div>
            </div>

            {/* Orbit guide + ring progress */}
            <div className="px-4 py-3 border-b border-white/10 flex-shrink-0">
              <p className="text-[10px] text-white/40 uppercase tracking-wide mb-3">Orbit Guide</p>
              <div className="flex items-center justify-center mb-3">
                <OrbitGuide />
              </div>
              <div className="space-y-2">
                {ORBIT_RINGS?.map((ring) => {
                  const captured = ringProgress?.[ring?.id] || 0;
                  const pct = Math.min(100, (captured / ring?.targetFrames) * 100);
                  return (
                    <div key={ring?.id} className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: ring?.color }} />
                      <div className="flex-1">
                        <div className="flex justify-between mb-0.5">
                          <span className="text-[10px] text-white/60">{ring?.label}</span>
                          <span className="text-[10px] text-white/40">{captured}/{ring?.targetFrames}</span>
                        </div>
                        <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-400"
                            style={{ width: `${pct}%`, backgroundColor: ring?.color }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Frame quality summary */}
            {frames?.length > 0 && (
              <div className="px-4 py-3 border-b border-white/10 flex-shrink-0">
                <p className="text-[10px] text-white/40 uppercase tracking-wide mb-2">Frame Quality</p>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-300"
                      style={{ width: `${avgQuality}%`, backgroundColor: getQualityColor(avgQuality) }} />
                  </div>
                  <span className="text-xs font-bold" style={{ color: getQualityColor(avgQuality) }}>{avgQuality}</span>
                </div>
                <div className="grid grid-cols-3 gap-1.5 text-center">
                  {[
                    { label: 'Good', count: goodCount, color: '#10b981' },
                    { label: 'Fair', count: frames?.filter(f => f?.quality >= QUALITY_FAIR && f?.quality < QUALITY_GOOD)?.length, color: '#f59e0b' },
                    { label: 'Poor', count: frames?.filter(f => f?.quality < QUALITY_FAIR)?.length, color: '#ef4444' },
                  ]?.map(({ label, count, color }) => (
                    <div key={label} className="bg-white/5 rounded-lg py-1.5">
                      <div className="text-sm font-bold" style={{ color }}>{count}</div>
                      <div className="text-[9px] text-white/40">{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent frames strip */}
            {frames?.length > 0 && (
              <div className="px-4 py-3 border-b border-white/10 flex-shrink-0">
                <p className="text-[10px] text-white/40 uppercase tracking-wide mb-2">Recent Frames</p>
                <div className="flex gap-1.5 overflow-x-auto pb-1">
                  {frames?.slice(-8)?.map((frame, i) => (
                    <div key={i} className="relative flex-shrink-0 w-12 h-12 rounded-md overflow-hidden border border-white/10">
                      <img src={frame?.dataUrl} alt={`Frame ${i}`} className="w-full h-full object-cover" />
                      <div className="absolute bottom-0 left-0 right-0 h-1"
                        style={{ backgroundColor: getQualityColor(frame?.quality) }} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Scanning instructions */}
            {!isComplete && (
              <div className="px-4 py-3 flex-shrink-0">
                <p className="text-[10px] text-white/40 uppercase tracking-wide mb-2">How to scan</p>
                <div className="space-y-2">
                  {[
                    { icon: '📦', text: 'Resize the cube to fit your dish' },
                    { icon: '⬆', text: 'Start from above the dish' },
                    { icon: '🔄', text: 'Slowly orbit around it at your own pace' },
                    { icon: '📐', text: 'Lower angle with each orbit' },
                    { icon: '💡', text: 'Keep dish well-lit & steady' },
                    { icon: '✅', text: 'Press Finish when done — no time limit' },
                  ]?.map(({ icon, text }, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-sm">{icon}</span>
                      <span className="text-[11px] text-white/50">{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Export actions */}
            {isComplete && (
              <div className="p-4 space-y-2 flex-shrink-0 mt-auto">
                <p className="text-[10px] text-white/40 text-center mb-3">
                  {totalFrames} frames ready · {formatDuration(scanDuration)} scan
                </p>
                <button onClick={handleExport}
                  className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-semibold text-sm py-2.5 rounded-xl transition-colors">
                  <Icon name="Link" size={14} color="white" />
                  Link to Dish
                </button>
                <button onClick={handleDownloadZip} disabled={isZipping}
                  className="w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/10 font-medium text-sm py-2.5 rounded-xl transition-colors disabled:opacity-50">
                  {isZipping
                    ? <><Icon name="Loader" size={14} color="white" className="animate-spin" /> Creating ZIP...</>
                    : <><Icon name="Package" size={14} color="white" /> Download for Luma AI</>
                  }
                </button>
                <p className="text-[10px] text-white/30 text-center">
                  ZIP includes {totalFrames} JPEG frames + metadata.json
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DishScannerModal;
